import Vue from "vue";
import Router from "vue-router";
import Swal from "sweetalert2";
import { isEmpty } from "lodash";

import DashboardLayout from "@/pages/Dashboard/Layout/DashboardLayout.vue";
import AuthLayout from "@/pages/Dashboard/Pages/AuthLayout.vue";

// Pages
import Dashboard from "@/pages/Dashboard/Dashboard.vue";
import RedirectPage from "@/pages/Dashboard/Redirect.vue";
import User from "@/pages/Dashboard/Pages/UserProfile.vue";

import Login from "@/pages/Dashboard/Pages/Login.vue";

import Users from "@/pages/Users/Users.vue";
import Products from "@/pages/Products/Products.vue";
import Suppliers from "@/pages/Suppliers/Suppliers.vue";
import Offers from "@/pages/Offers/Offers.vue";
import GeneralCosts from "@/pages/GeneralCosts/GeneralCosts.vue";
import OqsSettings from "@/pages/OqsSettings/OqsSettings.vue";
import ParcelCalculator from "@/pages/ParcelCalculator/ParcelCalculator.vue";
import Returns from "@/pages/Returns/Returns.vue";
import PhotoManager from "@/pages/Photos/PhotoManager.vue";
import ReturnPhotoTypes from "@/pages/Returns/ReturnPhotoTypes.vue";
import Orders from "@/pages/Orders/Orders.vue";
import RulesSqp from "@/pages/RulesSqp/RulesSqp.vue";
import StreetDirectory from "@/pages/Orders/StreetDirectory.vue";

import ShippingPlans from "@/pages/ShippingPlans/ShippingPlans.vue";
import Historical from "@/pages/ShippingPlans/Historical.vue";

import Procurement from "@/pages/Procurement/Procurement.vue";
import ProcurementDashboard from "@/pages/Procurement/ProcurementDashboard.vue";

import WmsDashboard from "@/pages/Wms/Dashboard.vue";
import WmsInventories from "@/pages/Wms/Inventories.vue";
import WmsWarehouses from "@/pages/Wms/Warehouses.vue";
import WmsStorageLocations from "@/pages/Wms/StorageLocations.vue";
import InventoryManagementExport from "@/pages/Wms/InventoryManagementExport.vue";

import { isAuthenticated, isAdmin } from "../mixins/auth";
import TokenService from "../services/token.service";

import i18n from "@/i18n/index.js";
import History from "../pages/Wms/History.vue";

let authPages = {
  path: "/",
  component: AuthLayout,
  name: "Authentication",
  children: [
    {
      path: "/login",
      name: "Login",
      component: Login,
    },
  ],
};

// Der Mobilbereich wird bewusst per import() geladen: die Desktop-Routen haengen alle statisch im
// Haupt-Bundle, ein Handy soll davon nichts ziehen muessen. Rechte kommen aus denselben
// Menue-Schluesseln wie am Desktop (siehe Guard unten), es gibt keine eigene Rechte-Logik.
let mobilePages = {
  path: "/m",
  component: () => import(/* webpackChunkName: "mobile" */ "@/pages/Mobile/MobileLayout.vue"),
  children: [
    {
      path: "",
      redirect: "/m/lieferplan",
    },
    {
      path: "lieferplan",
      name: "MobileShippingPlans",
      component: () => import(/* webpackChunkName: "mobile" */ "@/pages/Mobile/ShippingPlanList.vue"),
      meta: {
        title: "layout.shippingPlans",
        authorize: { menu: "ShippingPlans" },
      },
    },
    {
      path: "lieferplan/:id",
      name: "MobileShippingPlanDetail",
      component: () => import(/* webpackChunkName: "mobile" */ "@/pages/Mobile/ShippingPlanDetail.vue"),
      meta: {
        title: "layout.shippingPlans",
        // Ziel des Zurueck-Pfeils: die uebergeordnete Liste, unabhaengig davon, woher der Nutzer kam
        mobileBack: "/m/lieferplan",
        authorize: { menu: "ShippingPlans" },
      },
    },
    {
      path: "retoure",
      name: "MobileReturns",
      component: () => import(/* webpackChunkName: "mobile" */ "@/pages/Mobile/ReturnList.vue"),
      meta: {
        title: "returns.title",
        authorize: { menu: "Returns" },
      },
    },
    {
      path: "retoure/neu",
      name: "MobileReturnCapture",
      component: () => import(/* webpackChunkName: "mobile" */ "@/pages/Mobile/ReturnCapture.vue"),
      meta: {
        title: "returns.new",
        mobileBack: "/m/retoure",
        authorize: { menu: "Returns" },
      },
    },
    {
      path: "retoure/:id",
      name: "MobileReturnDetail",
      component: () => import(/* webpackChunkName: "mobile" */ "@/pages/Mobile/ReturnDetail.vue"),
      meta: {
        title: "returns.title",
        mobileBack: "/m/retoure",
        authorize: { menu: "Returns" },
      },
    },
    {
      // Kachelseite (Trello 21.08.): bewusst OHNE authorize - sie zeigt nur die erlaubten
      // Kacheln; die Unterseiten tragen ihre eigenen Menue-Rechte
      path: "lager",
      name: "MobileWarehouse",
      component: () => import(/* webpackChunkName: "mobile" */ "@/pages/Mobile/Wms/MWmsHome.vue"),
      meta: { title: "layout.wms.wms" },
    },
    {
      path: "lager/bestand",
      name: "MobileWmsInventories",
      component: () => import(/* webpackChunkName: "mobile" */ "@/pages/Mobile/Wms/MInventories.vue"),
      meta: { title: "layout.wms.inventory", mobileBack: "/m/lager", authorize: { menu: "Inventories" } },
    },
    {
      path: "lager/wms",
      name: "MobileWmsWarehouses",
      component: () => import(/* webpackChunkName: "mobile" */ "@/pages/Mobile/Wms/MWarehouses.vue"),
      meta: { title: "layout.wms.warehouses", mobileBack: "/m/lager", authorize: { menu: "Warehouses" } },
    },
    {
      path: "lager/wareneingang",
      name: "MobileWmsIncomingGoods",
      component: () => import(/* webpackChunkName: "mobile" */ "@/pages/Mobile/Wms/MWareneingang.vue"),
      meta: { title: "mobile.wms.incomingGoods", mobileBack: "/m/lager", authorize: { menu: "Warehouses" } },
    },
    {
      path: "lager/umlagerung",
      name: "MobileWmsMovement",
      component: () => import(/* webpackChunkName: "mobile" */ "@/pages/Mobile/Wms/MUmlagerung.vue"),
      meta: { title: "mobile.wms.movement", mobileBack: "/m/lager", authorize: { menu: "Warehouses" } },
    },
    {
      path: "lager/anpassung",
      name: "MobileWmsAdjustment",
      component: () => import(/* webpackChunkName: "mobile" */ "@/pages/Mobile/Wms/MAnpassung.vue"),
      meta: { title: "mobile.wms.manualAdjustment", mobileBack: "/m/lager", authorize: { menu: "Warehouses" } },
    },
    {
      // Inventur = Manuelle Anpassung mit fixiertem Protokolltyp "Zaehlung" (Trello 21.08.)
      path: "lager/inventur",
      name: "MobileWmsStocktaking",
      component: () => import(/* webpackChunkName: "mobile" */ "@/pages/Mobile/Wms/MAnpassung.vue"),
      props: { inventurModus: true },
      meta: { title: "mobile.wms.stocktaking", mobileBack: "/m/lager", authorize: { menu: "Warehouses" } },
    },
    {
      path: "lager/lagerplaetze",
      name: "MobileWmsStorageLocations",
      component: () => import(/* webpackChunkName: "mobile" */ "@/pages/Mobile/Wms/MLagerplaetze.vue"),
      meta: { title: "layout.wms.storagelocations", mobileBack: "/m/lager", authorize: { menu: "StorageLocations" } },
    },
    {
      path: "suche",
      name: "MobileSearch",
      component: () => import(/* webpackChunkName: "mobile" */ "@/pages/Mobile/ComingSoon.vue"),
      meta: { title: "mobile.search" },
    },
    {
      path: "zahlen",
      name: "MobileFigures",
      component: () => import(/* webpackChunkName: "mobile" */ "@/pages/Mobile/ComingSoon.vue"),
      meta: { title: "mobile.figures" },
    },
  ],
};

Vue.use(Router);

const router = new Router({
  mode: "history",
  base: process.env.BASE_URL,
  scrollBehavior: (to) => {
    if (to.hash) {
      return { selector: to.hash };
    } else {
      return { x: 0, y: 0 };
    }
  },
  linkExactActiveClass: "nav-item active",
  routes: [
    {
      path: "/",
      redirect: "/login",
      name: "Home",
      meta: {
        title: "Login",
      },
    },
    authPages,
    mobilePages,
    {
      path: "/app",
      component: DashboardLayout,
      children: [
        {
          path: "dashboard",
          name: "Dashboard",
          components: { default: Dashboard },
          meta: {
            title: "Dashboard",
          },
        },
        {
          path: "redirect",
          name: "RedirectPage",
          component: RedirectPage,
          meta: {
            title: "Dashboard",
          },
        },
        {
          path: "suppliers",
          name: "Suppliers",
          components: { default: Suppliers },
          meta: {
            title: "Suppliers",
            authorize: {
              menu: "Suppliers",
            },
          },
        },
        {
          path: "parcel-calculator",
          name: "ParcelCalculator",
          components: { default: ParcelCalculator },
          meta: {
            title: "parcelCalculator.title",
            authorize: {
              menu: "ParcelCalculator",
            },
          },
        },
        {
          path: "returns",
          name: "Returns",
          components: { default: Returns },
          meta: {
            title: "returns.title",
            authorize: {
              menu: "Returns",
            },
          },
        },
        {
          // Foto Manager (Trello 26.08.2026): alle Bilder quellenuebergreifend
          path: "photos",
          name: "PhotoManager",
          components: { default: PhotoManager },
          meta: {
            title: "photos.title",
            authorize: {
              menu: "PhotoManager",
            },
          },
        },
        {
          path: "return-photo-types",
          name: "ReturnPhotoTypes",
          components: { default: ReturnPhotoTypes },
          meta: {
            title: "returns.photoTypesPage.title",
            authorize: {
              menu: "ReturnPhotoTypes",
            },
          },
        },
        {
          path: "orders",
          name: "Orders",
          components: { default: Orders },
          meta: {
            title: "Orders",
            authorize: {
              menu: "Orders",
            },
          },
        },
        {
          path: "rules-sqp",
          name: "RulesSqp",
          components: { default: RulesSqp },
          meta: {
            title: "Rules SQP",
            authorize: {
              menu: "RulesSqp",
            },
          },
        },
        {
          path: "street-directory",
          name: "StreetDirectory",
          components: { default: StreetDirectory },
          meta: {
            title: "Street Directory",
            authorize: {
              menu: "StreetDirectory",
            },
          },
        },
        {
          path: "products",
          name: "Products",
          components: { default: Products },
          meta: {
            title: "Products",
            authorize: {
              menu: "Products",
            },
          },
        },
        {
          path: "offers",
          name: "Offers",
          components: { default: Offers },
          meta: {
            title: "Offers",
            authorize: {
              menu: "Offers",
            },
          },
        },
        {
          path: "oqs-settings",
          name: "OqsSettings",
          components: { default: OqsSettings },
          meta: {
            title: "OqsSettings",
            authorize: {
              menu: "OqsSettings",
            },
          },
        },
        {
          path: "general-costs",
          name: "GeneralCosts",
          components: { default: GeneralCosts },
          meta: {
            title: "GeneralCosts",
            authorize: {
              menu: "GeneralCosts",
            },
          },
        },
        {
          path: "users",
          name: "Users",
          components: { default: Users },
          meta: {
            title: "Users",
          },
        },
        {
          path: "user-profile",
          name: "User Profile",
          components: { default: User },
          meta: {
            title: "User profile - Users",
          },
        },
        {
          path: "procurement",
          name: "Procurement",
          components: { default: Procurement },
          meta: {
            title: "Procurement",
            authorize: {
              menu: "Procurement",
            },
          },
        },
        {
          path: "procurement-dashboard",
          name: "ProcurementDashboard",
          components: { default: ProcurementDashboard },
          meta: {
            title: "Procurement Dashboard",
            authorize: {
              menu: "ProcurementDashboard",
            },
          },
        },
        {
          path: "shipping-plans",
          name: "ShippingPlans",
          components: { default: ShippingPlans },
          meta: {
            title: "Shipping Plan",
            authorize: {
              menu: "ShippingPlans",
            },
          },
        },
        {
          path: "historical",
          name: "ShippingPlans-Historical",
          components: { default: Historical },
        },
        {
          path: "wms-dashboard",
          name: "WmsDashboard",
          components: { default: WmsDashboard },
          meta: {
            title: "Warehouse Dashboard",
            authorize: {
              menu: "WmsDashboard",
            },
          },
        },
        {
          path: "wms-inventories",
          name: "Inventories",
          components: { default: WmsInventories },
          meta: {
            title: "Warehouse Inventories",
            authorize: {
              menu: "Inventories",
            },
          },
        },
        {
          path: "wms-warehouses",
          name: "Warehouses",
          components: { default: WmsWarehouses },
          meta: {
            title: "Warehouses",
            authorize: {
              menu: "Warehouses",
            },
          },
        },
        {
          path: "wms-storage-locations",
          name: "StorageLocations",
          components: { default: WmsStorageLocations },
          meta: {
            title: "Storage Locations",
            authorize: {
              menu: "StorageLocations",
            },
          },
        },
        {
          path: "wms-management-export",
          name: "InventoryManagementExport",
          components: { default: InventoryManagementExport },
          meta: {
            title: "Inventory Management Export",
            authorize: {
              menu: "InventoryManagementExport",
            },
          },
        },
        {
          path: "wms-history",
          name: "History",
          components: { default: History },
          meta: {
            title: "History",
            authorize: {
              menu: "WarehousesHistory",
            },
          },
        },
      ],
    },
  ],
});

const DEFAULT_TITLE = "Operations Control Center by CS-Trading";
router.beforeEach((to, from, next) => {
  if (to.path.startsWith("/app") || to.path.startsWith("/m")) {
    if (!isAuthenticated()) {
      return next({ path: "/login", query: { returnUrl: to.path } });
    }
  }

  const { authorize } = to.meta;
  if (authorize && authorize.menu && !isAdmin()) {
    const user = TokenService.getUser();
    if (!user.menuPermissions || !user.menuPermissions.includes(authorize.menu) || isEmpty(user.tableColumnPermissions) || isEmpty(user.tableColumnPermissions[authorize.menu])) {
      Swal.fire({
        title: i18n.t("permissionDenied"),
        text: i18n.t("permissionDeniedMessage"),
        icon: "error",
        confirmButtonText: "OK",
      });
      // Im Mobilbereich bleiben, sonst landet man mitten in der Desktop-Ansicht. Ziel ist
      // bewusst eine Route ohne authorize-Meta, damit keine Weiterleitungsschleife entsteht.
      if (to.path.startsWith("/m")) {
        return next({ path: "/m/suche" });
      }

      return next({ path: "/app/redirect", query: { to: "/app/dashboard", error: "noPermission" } });
    }
  }

  Vue.nextTick(() => {
    // Die Mobilrouten fuehren i18n-Schluessel als Titel; die Desktop-Routen Klartext.
    // i18n.te() unterscheidet beides, ohne die vorhandenen Titel anzufassen.
    const raw = to.meta && to.meta.title;
    const title = raw && i18n.te(raw) ? i18n.t(raw) : raw;
    document.title = (title && `${title} - ${DEFAULT_TITLE}`) || DEFAULT_TITLE;
  });

  next();
});

export default router;
